import * as React from "react";
import { FormattedMessage } from "react-intl";
import { Link } from "react-router-dom";
import { useLocation, useHistory } from "react-router";
import { FEATURE_FLAGS_ENUM } from "../../../constants/featureFlag";
import { useAppContext } from "../../../hooks/useAppContext";
import { canAccessGroupsRoute } from "src/domains/group/permissions";
import { canAccessSpacesRoute } from "src/domains/spaces/permissions";
import { canAccessOrganizationRoute } from "src/domains/organization/permissions";
import { canAccessDeveloperRoute } from "src/domains/developer/permissions";
import { Styled } from "./styles";
import { isUuid } from "@screencloud/uuid";
import { useCustomer } from "../../../billinglatest/hooks/useCustomer";
import {
  Button,
  Dropdown,
  DropdownMenu,
  DropdownItem,
  Icon,
} from "@screencloud/screencloud-ui-components";
import { openSafeUrl } from "src/helpers/urlHelper";

interface Nav {
  text: string | React.ReactNode;
  route: string;
  isVisible: boolean;
  isSubMenu?: boolean;
  icon?: React.ReactNode;
  target?: string;
}
interface AdminNavProps {
  customTabs?: Nav[];
  className?: string;
}

export const AdminNav = (props: AdminNavProps) => {
  const context = useAppContext();
  const displayCustomTabs = (props.customTabs?.length ?? 0) > 0;
  const customer = useCustomer();
  const history = useHistory();
  // Only get customer if the main Admin Nav is showing
  // The ability to hide the billing tab depends on this
  React.useEffect(() => {
    const fetchCustomer = async () => {
      try {
        await customer.fetch();
      } catch (error) {
        console.error(
          "There was a problem getting your customer details. Please try again.",
        );
      }
    };
    if (!displayCustomTabs) {
      fetchCustomer();
    }
  }, [displayCustomTabs]);

  const getNav = (nav: Nav) => {
    const location = useLocation();
    const route = location.pathname.split("/")[1];
    const subRoute = location.pathname.split("/")[2];
    let isSelected = route === nav.route.split("/")[1];

    const linkText = typeof nav.text === "string" ? nav.text : "";

    if (
      displayCustomTabs &&
      subRoute !== "" &&
      !isUuid(subRoute) &&
      route !== "screens-manager" &&
      route !== "screens-summary" &&
      route !== "usb-provisioning"
    ) {
      isSelected = subRoute === nav.route.split("/")[2];
    }

    if (route === "playback-logs" && nav.route === "/proof-of-play") {
      isSelected = true;
    }

    // External link
    if (nav.route.startsWith("http")) {
      return (
        <li className="items-item" key={nav.route}>
          <a
            href={nav.route}
            className={`items-link ${isSelected ? "active" : ""}`}
            data-testid={`item-${nav.route.replace(/ /g, "-")}`}
            target={nav.target ?? ""}
          >
            {nav.icon && nav.icon}
            {nav.text}
          </a>
        </li>
      );
    }

    // Internal link
    return (
      <li className="items-item" key={linkText}>
        <Link
          to={nav.route}
          className={`items-link ${isSelected ? "active" : ""}`}
          data-testid={`item-${linkText.replace(/ /g, "-")}`}
        >
          {nav.icon && nav.icon}
          {/* Removes whitespace from FormattedMessage ID to make sure ID is valid*/}
          {/* Example: 'groups & roles' will become 'groups&roles' */}
          <FormattedMessage
            id={`admin.${linkText.replace(/ /g, "")}`}
            defaultMessage={linkText}
          />
        </Link>
      </li>
    );
  };

  const getSubMenuDropdownItem = (nav: Nav) => {
    const location = useLocation();
    const route = location.pathname.split("/")[1];
    const subRoute = location.pathname.split("/")[2];
    let isSelected = route === nav.route.split("/")[1];

    const linkText = typeof nav.text === "string" ? nav.text : "";

    if (
      displayCustomTabs &&
      subRoute !== "" &&
      !isUuid(subRoute) &&
      route !== "screens-manager" &&
      route !== "screens-summary" &&
      route !== "usb-provisioning"
    ) {
      isSelected = subRoute === nav.route.split("/")[2];
    }

    if (route === "playback-logs" && nav.route === "/proof-of-play") {
      isSelected = true;
    }

    // External link dropdown item
    if (nav.route.startsWith("http")) {
      return (
        <DropdownItem
          key={nav.route}
          onClick={() => openSafeUrl(nav.route, nav.target ?? "_self")}
          className={isSelected ? "active" : ""}
        >
          <div className="dropdown-item-container">
            <div className="dropdown-item-content">
              {nav.icon && nav.icon}
              {nav.text}
            </div>
            <Icon name="arrow-up-right-circle" className="dropdown-item-icon" />
          </div>
        </DropdownItem>
      );
    }

    // Internal link dropdown item
    return (
      <DropdownItem
        key={linkText}
        onClick={() => history.push(nav.route)}
        className={isSelected ? "active" : ""}
      >
        <div className="dropdown-item-container">
          <div className="dropdown-item-content">
            {nav.icon && nav.icon}
            <FormattedMessage
              id={`admin.${linkText.replace(/ /g, "")}`}
              defaultMessage={linkText}
            />
          </div>
          <Icon name="arrow-up-right-circle" className="dropdown-item-icon" />
        </div>
      </DropdownItem>
    );
  };

  const renderNav = () => {
    const shouldOrgTabVisible = canAccessOrganizationRoute(context);

    const shoudBillingTabVisible =
      context.currentPermissions.validateAnySpace("billing", "manage") &&
      !context.currentOrg?.isDraft &&
      !customer.get().partner;
    const shouldGroupsAndRolesTabVisible =
      context.currentPermissions.validateOrg("group", "read") &&
      context.shouldShowFeature(FEATURE_FLAGS_ENUM.CUSTOM_PERMISSION_SETS);
    const shouldCredentialsTabVisible =
      context.currentPermissions.validateOrg("credentials" as any, "read") &&
      context.shouldShowFeature(FEATURE_FLAGS_ENUM.CREDENTIALS_SETTINGS);
    const shouldThemeTabVisible =
      context.currentPermissions.validateOrg("theme", "read") &&
      (context.currentPermissions.validateOrg("theme", "update") ||
        context.currentPermissions.validateOrg("theme", "create") ||
        context.currentPermissions.validateOrg("theme", "delete"));
    const shouldDeveloperTabVisible = canAccessDeveloperRoute(context);

    const shouldGroupTabVisible = canAccessGroupsRoute(context);

    const shouldSpaceTabVisible = canAccessSpacesRoute(context);

    let navs: JSX.Element[] = [];
    let subMenuDropdown: JSX.Element | null = null;

    if (displayCustomTabs) {
      const visibleTabs =
        props.customTabs?.filter((nav) => nav.isVisible) ?? [];
      const regularTabs = visibleTabs.filter((nav) => !nav.isSubMenu);
      const subMenuTabs = visibleTabs.filter((nav) => nav.isSubMenu);

      // Render regular tabs
      navs = regularTabs.map((nav) => getNav(nav));

      // Render submenu dropdown if there are submenu items
      if (subMenuTabs.length > 0) {
        subMenuDropdown = (
          <li className="items-item submenu-dropdown" key="submenu-dropdown">
            <Dropdown
              direction="right"
              className="admin-nav-submenu"
              icon={
                <Button icon inline className="submenu-trigger">
                  <Icon name="dots" />
                </Button>
              }
            >
              <DropdownMenu>
                {subMenuTabs.map((nav) => getSubMenuDropdownItem(nav))}
              </DropdownMenu>
            </Dropdown>
          </li>
        );
      }
    } else {
      navs =
        [
          {
            text: "account",
            route: "/account",
            isVisible: true,
          },
          {
            text: "organization",
            route: "/organization",
            isVisible: shouldOrgTabVisible,
          },
          {
            text: "look & feel",
            route: "/themes",
            isVisible: shouldThemeTabVisible,
          },
          {
            text: "billing",
            route: context.hasSbbFlag() ? "/billing" : "/billing/latest",
            isVisible: shoudBillingTabVisible,
          },
          {
            text: "people",
            route: "/people",
            isVisible: context.currentPermissions.validateOrg("user", "manage"),
          },
          {
            text: shouldGroupsAndRolesTabVisible ? "groups & roles" : "groups",
            route: "/groups",
            isVisible: shouldGroupTabVisible,
          },
          {
            text: "spaces",
            route: "/spaces",
            isVisible: shouldSpaceTabVisible,
          },
          {
            text: "developer",
            route: "/developer",
            isVisible: shouldDeveloperTabVisible,
          },
          {
            text: "credentials",
            route: "/credentials",
            isVisible: shouldCredentialsTabVisible,
          },
          {
            text: "proof of play",
            route: "/proof-of-play",
            isVisible: context.currentPermissions.validateAnySpace(
              "playback_log",
              "read",
            ),
          },
          {
            text: "audit logs",
            route: "/logs",
            isVisible: context.currentPermissions.validateOrg("org", "audit"),
          },
        ]
          .filter((nav) => nav.isVisible)
          .map((nav) => getNav(nav)) ?? [];
    }

    return { navs, subMenuDropdown };
  };

  const { navs, subMenuDropdown } = renderNav();
  const totalNavItems = navs.length + (subMenuDropdown ? 1 : 0);

  if (totalNavItems < 2) {
    return null;
  }

  return (
    <Styled className={props.className} isCustomTabs={displayCustomTabs}>
      <div className="wrapper">
        <ul className="items">
          {navs}
          {subMenuDropdown}
        </ul>
      </div>
    </Styled>
  );
};

export default AdminNav as React.ComponentType<AdminNavProps>;
